import {ClickOutside} from 'ngxtension/click-outside';

import {Direction} from '@angular/cdk/bidi';
import {NgClass, NgTemplateOutlet} from '@angular/common';
import {ChangeDetectionStrategy, Component, computed, input, InputSignal, signal} from '@angular/core';
import {MatTooltip, TooltipPosition} from '@angular/material/tooltip';
import {TranslateModule} from '@ngx-translate/core';

import {SpeedDialAction, SpeedDialButtonConfig, SpeedDialDirectionType} from './types';

@Component({
	selector: 'dc-speed-dial',
	imports: [NgClass, NgTemplateOutlet, MatTooltip, TranslateModule, ClickOutside],
	templateUrl: './speed-dial.component.html',
	styleUrl: './speed-dial.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	host: {
		'[class]': 'sizeClass()',
	},
})
export class SpeedDialComponent {
	/* ------------------------------------------  Types and Constants ------------------------------------------ */

	/* ------------------------------------------ PROVIDERS / SERVICES ------------------------------------------ */

	/* ------------------------------------------------  Inputs ------------------------------------------------ */
	/** The direction of the speed dial
	 *  @type {SpeedDialDirectionType}
	 */
	public readonly direction: InputSignal<SpeedDialDirectionType> = input.required<SpeedDialDirectionType>();
	/** The direction to flip the speed dial when the app direction is **/
	public readonly autoDirectionFlip: InputSignal<Direction> = input<Direction>();
	/** The list of actions to display */
	public readonly actions: InputSignal<SpeedDialAction[]> = input.required<SpeedDialAction[]>();
	public readonly data: InputSignal<unknown> = input<unknown>();
	/** The button configuration */
	public readonly buttonConfig = input<SpeedDialButtonConfig, Partial<SpeedDialButtonConfig>>(
		{
			icon: 'fad fa-circle-ellipsis text-primary-500 fa-fw',
			iconClass: 'transition-transform duration-300 ease-in-out',
			openClass: 'rotate-90',
		} as SpeedDialButtonConfig,
		{
			transform: (value: Partial<SpeedDialButtonConfig>) => {
				return {
					...value,
					icon: value?.icon || 'fad fa-circle-ellipsis text-primary-500 fa-fw',
					iconClass: value?.iconClass || 'transition-transform duration-300 ease-in-out',
					openClass: value?.openClass || 'rotate-90',
				};
			},
		},
	);
	/** The size class of the button */
	public readonly sizeClass: InputSignal<`h-${number | string} w-${number | string}`> = input('h-8 w-8');
	/** The tooltip to display */
	public tooltip: InputSignal<string> = input<string>();
	/* ------------------------------------------------  Outputs ------------------------------------------------ */

	/* ------------------------------------------------  Signals ------------------------------------------------ */
	protected readonly computedDirection$ = computed<SpeedDialDirectionType>(() => {
		const autoDirectionFlip = this.autoDirectionFlip();
		const direction = this.direction();
		if (!autoDirectionFlip) return direction;
		const appDirection = document.documentElement.getAttribute('dir') as 'ltr' | 'rtl';
		if (appDirection !== autoDirectionFlip && ['right', 'left'].includes(direction)) {
			return direction === 'right' ? 'left' : 'right';
		}
		return direction;
	});
	protected readonly actions$ = computed(() => {
		return this.actions()?.map(action => {
			const iconConfig = typeof action.iconConfig === 'string' ? {icon: action.iconConfig} : action.iconConfig;
			return {
				...action,
				iconConfig,
			};
		});
	});
	protected readonly tooltipPosition$ = computed<TooltipPosition>(() => {
		const speedDialDirection = this.computedDirection$();
		const appDirection = document.documentElement.getAttribute('dir') as 'ltr' | 'rtl';
		switch (speedDialDirection) {
			case 'up':
			case 'down':
				return appDirection === 'rtl' ? 'before' : 'after';
			case 'left':
			case 'right':
				return 'above';
		}
	});
	/* -------------------------------------------------- Data -------------------------------------------------- */
	protected readonly isOpen$ = signal<boolean>(false);

	/* ------------------------------------------------  Constructor ------------------------------------------------ */
	constructor() {}

	/* ----------------------------------------------- Lifecycle Hooks ----------------------------------------------- */

	/* ------------------------------------------------  Methods ------------------------------------------------ */
	protected onClickOutside(): void {
		if (this.isOpen$()) {
			this.isOpen$.set(false);
		}
	}

	protected toggleMenu(): void {
		this.isOpen$.set(!this.isOpen$());
	}

	protected handleClick($event: MouseEvent, actionBtn: SpeedDialAction): void {
		actionBtn.action($event, this.data());
		this.isOpen$.set(false);
	}
}
